import * as Yup from 'yup';
import FileUploader from './file-uploader';

class EcodigoForm {
  triggerErrors: boolean;
  readonly formWrapper: HTMLElement;
  readonly form: HTMLFormElement;
  readonly grecaptcha: unknown;
  readonly recaptchaToken: string | null | undefined;
  fileUploader: FileUploader | null = null;
  readonly fields: string[];
  private readonly schema: unknown;
  private fileUpload: HTMLElement | null = null;

  constructor(
    formWrapper: HTMLElement,
    form: HTMLFormElement,
    fields: string[],
    schema: unknown,
    grecaptcha: unknown,
    recaptchaToken: string | null | undefined,
    triggerErrors: boolean = false,
  ) {
    this.formWrapper = formWrapper;
    this.fields = fields;
    this.schema = schema;
    this.triggerErrors = triggerErrors;
    this.form = form;
    this.grecaptcha = grecaptcha;
    this.recaptchaToken = recaptchaToken;
  }

  init = () => {
    this.fields.forEach((fieldName) => {
      const input = this.formWrapper.querySelector(`[name=${fieldName}]`) as HTMLInputElement;
      input.addEventListener('blur', () => {
        this.validateField(fieldName, input.value);
      });
      input.addEventListener('input', () => {
        this.validateField(fieldName, input.value);
      });
    });

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitForm();
    });

    this.fileUploader = null;
    this.fileUpload = this.formWrapper?.querySelector('.file-upload') as HTMLElement | null;
    if (this.fileUpload) {
      const maxFiles = this.fileUpload.getAttribute('data-max-files')
        ? parseInt(this.fileUpload.getAttribute('data-max-files') || '0', 10)
        : 3;
      const maxFileSize = this.fileUpload.getAttribute('data-max-file-size')
        ? parseInt(this.fileUpload.getAttribute('data-max-file-size') || '0', 10)
        : 2;

      // Initialize the uploader
      this.fileUploader = new FileUploader(this.fileUpload, {
        maxFiles: maxFiles,
        maxSizeInMB: maxFileSize,
      });

      this.fileUploader.addEventListener('error', (message) => {
        if (typeof message === 'string') {
          this.showError('files', message);
        }
      });

      this.fileUploader.addEventListener('change', () => {
        if (this.triggerErrors) {
          this.showError('files', '');
          this.validateField('files', this.fileUploader?.files ?? []);
        }
      });
    }
  };

  showError = (fieldName: string, error: string): void => {
    const errorElement = this.formWrapper.querySelector(`[data-error="${fieldName}"]`);
    let inputElement = this.formWrapper.querySelector(`[name=${fieldName}]`);

    if (inputElement?.getAttribute('type') === 'file') {
      inputElement = inputElement.parentElement;
    }

    if (fieldName === 'global' && errorElement) {
      errorElement.classList.remove('d-none');
    }

    if (error) {
      if (errorElement) {
        errorElement.textContent = error;
      }
      if (inputElement !== null) {
        inputElement.classList.add('is-invalid');
        inputElement.classList.remove('is-valid');
      }
    } else {
      if (errorElement) {
        errorElement.textContent = '';
      }
      if (inputElement !== null) {
        inputElement.classList.remove('is-invalid');
        inputElement.classList.add('is-valid');
      }
    }
  };

  clearError = (fieldName: string): void => {
    const errorElement = this.formWrapper.querySelector(`[data-error="${fieldName}"]`);
    const inputElement = this.formWrapper.querySelector(`[name=${fieldName}]`);

    if (!errorElement || inputElement === null) return;

    errorElement.textContent = '';
    inputElement.classList.remove('is-invalid');
    inputElement.classList.remove('is-valid');

    const inputElementEvent = new Event('change');
    inputElement.dispatchEvent(inputElementEvent);
  };

  validateField = async (fieldName: string, value: unknown): Promise<boolean> => {
    try {
      const fieldSchema = Yup.object().shape({
        // @ts-expect-error fieldName is a dynamic key
        [fieldName]: this.schema.fields[fieldName as keyof typeof this.schema.fields],
      });

      await fieldSchema.validate(
        { [fieldName]: value },
        {
          abortEarly: false,
          context: this.getDataObject(),
        },
      );

      if (this.triggerErrors) {
        this.showError(fieldName, '');
      }
      return true;
    } catch (err: unknown) {
      if (err instanceof Yup.ValidationError && err.inner && err.inner.length && this.triggerErrors) {
        this.showError(fieldName, err.inner[0].message);
      }
      return false;
    }
  };

  submitForm = async (): Promise<boolean> => {
    return true;
  };

  getDataObject = () => {
    const dataObject: { [key: string]: string | unknown } = {};
    this.fields.forEach((fieldName) => {
      const input = this.form.querySelector(`[name=${fieldName}]`) as HTMLInputElement;
      dataObject[fieldName] = input.value;
    });
    dataObject.files = this.fileUploader?.files ?? [];
    return dataObject;
  };

  handleSubmitResponse = async (sendData: Response) => {
    const response = await sendData.json();

    if (sendData.status === 200) {
      this.form.querySelector('.ecodigo-form__wrapper')?.classList.add('d-none');
      this.form.querySelector('.ecodigo-form__success')?.classList.remove('d-none');
    } else {
      if (response.errors) {
        Object.entries(response.errors).forEach(([inputName, error]) => {
          this.showError(inputName, error as string);
        });
      } else {
        this.showError('global', 'Une erreur inconnue est survenue');
      }
    }
  };
}

export default EcodigoForm;
